import React, { useMemo } from 'react';
import styled from '@xstyled/styled-components';
import { t } from 'ttag';
import { Item } from '~/training/sequence/templates/items/Item';
import {
  TEXT,
  IMAGE,
  LAYOUT_LIST
} from '~/training/sequence/templates/items/itemIds';
import { Text } from '~/training/sequence/templates/items/text/Text';
import { Image } from '~/training/sequence/templates/items/image/Image';
import { Button } from '~/ui/buttons/Button';
import { useEditorStore } from '~/shell/Store';
import { usePreview } from '~/editor/hooks/usePreview';

const StartButton = styled(Button)`
  padding: 10px 42px 7px;
  font-family: ralewayBold;
  font-size: 14px;
  line-height: 1.5;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }
`;

const Container = styled.div<{ editable: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 40px 72px 26px;
  min-height: 600px;
  role: presentation;

  @media (max-width: 768px) {
    padding: 40px 22px 26px;
  }

  @media (max-height: 800px) {
    min-height: auto;
  }

  ${props =>
    props.editable
      ? `
  .chatbot-logo-image {
    pointer-events: auto;

    &:hover {
      & ~ .edit-bg-btn {
        opacity: 1;
      }
    }

    & ~ .edit-bg-btn {
      &:hover, &:focus {
        opacity: 1;
      }

      left: 50% !important;
      top: 50% !important;
      right: auto !important;
      bottom: auto !important;
      transform: translateX(-50%) translateY(-50%);
      opacity: 0;
    }
  }
    `
      : ''}
`;

const CenteredItemsContainer = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  role: presentation;
`;

const ContentContainer = styled.div`
  max-width: 600px;
  margin-bottom: 22px;

  h1 {
    font-size: 24px;
    line-height: 1.2;
    margin: 6px 0 5px;
  }

  .description-item {
    font-size: 14px;
    line-height: 1.5;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 22px;
`;

const DisclaimerText = styled.p`
  font-size: 11px;
  line-height: 1.5;
  color: #6f6f6f;

  @media (max-width: 768px) {
    font-size: 9px;
  }
`;

export const StartScreen = ({
  id,
  title,
  description,
  picture,
  onStartChat
}: {
  id: string;
  title?: string;
  description?: string;
  picture?: {
    id: string;
    name: string;
    mediaUrl: string;
  };
  onStartChat: () => void;
}) => {
  const { editorMode } = useEditorStore();
  const { openPreview } = usePreview();

  const handleStartChat = () => {
    if (editorMode) {
      openPreview({ queryParams: { skipStartScreen: 'true' } });
    } else {
      onStartChat();
    }
  };
  const titleItem = useMemo(
    () =>
      new Text({
        id: `title_${id}`,
        type: TEXT,
        values: {
          variant: 'heading1',
          value: title || ''
        },
        items: []
      }),
    []
  );

  const descriptionItem = useMemo(
    () =>
      new Text({
        id: `body_${id}`,
        type: TEXT,
        values: {
          value: description || '',
          variant: 'text',
          className: 'description-item'
        },
        items: []
      }),
    []
  );

  const imageItem = useMemo(() => {
    return new Image(
      {
        id: picture?.id || `${id}_placeholder`,
        type: IMAGE,
        values: {
          className: 'chatbot-logo-image',
          url: picture?.mediaUrl || '',
          alt: picture?.name || '',
          borderRadius: '50%',
          width: '52px',
          height: '52px'
        },
        items: []
      },
      { type: LAYOUT_LIST } as Item<typeof LAYOUT_LIST, unknown>
    );
  }, [picture, editorMode, id]);

  const TitleComponent = titleItem.component();
  const DescriptionComponent = descriptionItem.component();
  const ImageComponent = imageItem?.component();

  return (
    <Container editable={editorMode}>
      <CenteredItemsContainer>
        {ImageComponent && <ImageComponent key={`image_${id}`} />}
        <ContentContainer>
          <TitleComponent key={`title_${id}`} />
          <DescriptionComponent key={`body_${id}`} />
        </ContentContainer>
        <ButtonContainer>
          <StartButton onClick={handleStartChat}>{t`Start`}</StartButton>
        </ButtonContainer>
      </CenteredItemsContainer>
      <DisclaimerText>
        {t`This practice conversation is generated by AI to support your learning through practice. While the experience is designed to be helpful and generated dynamically, it may not cover every nuance and is not a substitute for your organization’s official policies. If you have any questions about responses, check with your employer.`}
      </DisclaimerText>
    </Container>
  );
};
